// Dispatcher for a Special Sponsorship program. Loads the program, then renders the roster component that
// fits its layout. Each sponsorship category has its own self-contained roster file under
// ./specialSponsorships so no single page grows fat — add a category by adding a roster and one line here.
import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import { useParams } from 'react-router-dom';
import PageLayout from '../components/common/PageLayout';
import { get } from '../utils/api';
import SareeRoster from './specialSponsorships/SareeRoster';
import VahanaRoster from './specialSponsorships/VahanaRoster';

// Map a loaded program to its roster component. pledge_driven programs (one auto-derived sponsor per slot,
// matched by product variation) use the flat pledge grid; everything else uses the grouped roster.
function rosterFor(program) {
  if (program?.pledge_driven) return SareeRoster;
  return VahanaRoster;
}

export default function SpecialSponsorshipProgramPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    const res = await get(`/api/manage/special-sponsorships/${encodeURIComponent(slug)}`, {}, { cache: 'no-store' });
    setData(res);
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try { await reload(); }
      catch (e) { if (!cancelled) setError(e?.message || 'Failed to load program'); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [reload]);

  if (loading) {
    return (
      <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
      </PageLayout>
    );
  }
  if (error || !data?.program) {
    return (
      <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
        <Alert severity="error" sx={{ m: 2 }}>{error || 'Program not found.'}</Alert>
      </PageLayout>
    );
  }

  const Roster = rosterFor(data.program);
  return <Roster slug={slug} data={data} reload={reload} />;
}
